import React, { useState, useEffect, useMemo } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Table,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Spinner,
  Badge,
} from 'reactstrap';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTechnicianInventory, fetchTechnicianInventoryHistory } from './actions/action';
import moment from 'moment';

const ViewItems = ({ isOpen, toggle, viewData }) => {
  const dispatch = useDispatch();
  const { technicianInventory, technicianInventoryHistory, isLoading } = useSelector((state) => state.service);
  
  const [activeTab, setActiveTab] = useState('inventory');
  const [activeSubTab, setActiveSubTab] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch inventory when offcanvas opens
  useEffect(() => {
    if (isOpen && viewData?.emp_id) {
      // Fetch Inventory
      fetchTechnicianInventory({
        data: { 
          technicianId: viewData.emp_id,
          country: 'ae' 
        },
        dispatch
      });

      // Fetch History
      fetchTechnicianInventoryHistory({
        data: { 
          technician_id: viewData.emp_id,
          country: 'ae' 
        },
        dispatch
      });

      setSearchTerm('');
      setActiveTab('inventory');
      setActiveSubTab(''); // Reset to ensure the next data set's first tab is picked
    }
  }, [isOpen, viewData, dispatch]);

  // Extract dynamic tabs from API response
  const inventoryData = technicianInventory?.data?.items || {};
  const subTabs = useMemo(() => Object.keys(inventoryData), [inventoryData]);

  // Set first dynamic tab as active when data arrives
  useEffect(() => {
    if (subTabs.length > 0) {
      setActiveSubTab(subTabs[0]);
    }
  }, [subTabs]);

  // Filter items based on active sub-tab and search term
  const filteredItems = useMemo(() => {
    if (!activeSubTab || !inventoryData[activeSubTab]) return [];
    
    const items = inventoryData[activeSubTab];
    return items.filter(item => 
      item?.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [inventoryData, activeSubTab, searchTerm]);

  // Filter history based on search term
  const filteredHistory = useMemo(() => {
    if (!Array.isArray(technicianInventoryHistory)) return [];
    return technicianInventoryHistory.filter(item => 
      item?.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.to_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.service_id?.toString().includes(searchTerm)
    );
  }, [technicianInventoryHistory, searchTerm]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleSubTab = (tab) => {
    if (activeSubTab !== tab) setActiveSubTab(tab);
  };

  const formatTabName = (name) => {
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ');
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '600px' }}
      className="border-start-0 shadow"
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom bg-light py-3">
        <div className="d-flex align-items-center">
          <i className="mdi mdi-information-outline text-primary me-2 fs-4"></i>
          <span className="fw-bold">Technician Details</span>
          <span className="mx-2 text-muted fw-normal">|</span>
          <span className="text-secondary fw-bold">{viewData?.emp_id || ''}</span>
        </div>
      </OffcanvasHeader>
      <OffcanvasBody className="p-0">
        <div className="bg-light px-4 pt-1 border-bottom">
          <Nav tabs className="nav-tabs-custom nav-justified border-bottom-0">
            <NavItem>
              <NavLink
                className={classnames({ 
                  active: activeTab === 'inventory',
                  'fw-bold': activeTab === 'inventory'
                })}
                onClick={() => toggleTab('inventory')}
                style={{ 
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '12px 20px',
                  color: activeTab === 'inventory' ? '#556ee6' : '#495057',
                  position: 'relative'
                }}
              >
                <i className={classnames("mdi mdi-briefcase-outline me-1", { "text-primary": activeTab === 'inventory' })}></i>
                Inventory
                {activeTab === 'inventory' && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                    height: '2px',
                    backgroundColor: '#556ee6'
                  }}></div>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ 
                  active: activeTab === 'history',
                  'fw-bold': activeTab === 'history'
                })}
                onClick={() => toggleTab('history')}
                style={{ 
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '12px 20px',
                  color: activeTab === 'history' ? '#556ee6' : '#495057',
                  position: 'relative'
                }}
              >
                <i className={classnames("mdi mdi-history me-1", { "text-primary": activeTab === 'history' })}></i>
                History
                {activeTab === 'history' && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                    height: '2px',
                    backgroundColor: '#556ee6'
                  }}></div>
                )}
              </NavLink>
            </NavItem>
          </Nav>
        </div>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="inventory" className="p-4">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading inventory...</p>
              </div>
            ) : (
              <>
                <div className="mb-4 bg-soft-light p-2 rounded-3">
                  <Nav 
                    pills 
                    className="nav-pills-custom flex-nowrap overflow-auto hide-scrollbar"
                    style={{
                      display: 'flex',
                      overflowX: 'auto',
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE/Edge
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    <style>
                      {`
                        .hide-scrollbar::-webkit-scrollbar {
                          display: none;
                        }
                      `}
                    </style>
                    {subTabs.map((tab) => (
                      <NavItem key={tab} className="text-center" style={{ flexShrink: 0 }}>
                        <NavLink
                          className={classnames({ active: activeSubTab === tab })}
                          onClick={() => toggleSubTab(tab)}
                          style={{ 
                            cursor: 'pointer', 
                            borderRadius: '8px', 
                            padding: '8px 20px',
                            fontSize: '13px',
                            fontWeight: activeSubTab === tab ? '600' : '500',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatTabName(tab)}
                        </NavLink>
                      </NavItem>
                    ))}
                    {subTabs.length === 0 && (
                      <div className="text-center w-100 py-2 text-muted small">
                        No inventory categories found
                      </div>
                    )}
                  </Nav>
                </div>

                <FormGroup className="mb-4">
                  <Label for="skuSearch" className="form-label fw-semibold text-muted small text-uppercase mb-2">
                    Search SKU
                  </Label>
                  <div className="position-relative">
                    <Input
                      type="text"
                      id="skuSearch"
                      placeholder="Type SKU or Title to search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control ps-5 py-2"
                      style={{ borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: 'none' }}
                    />
                    <i className="mdi mdi-magnify position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-5"></i>
                  </div>
                </FormGroup>

                <div className="table-responsive rounded-3 border-0">
                  {activeSubTab?.toLowerCase() === 'amc' ? (
                    <div className="inventory-accordion">
                      {filteredItems.length > 0 ? (
                        filteredItems.map((item, index) => (
                          <div key={index} className="card border shadow-none mb-2 overflow-hidden" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white p-0 border-0" id={`heading-inv-${index}`}>
                              <button 
                                className="btn btn-link w-100 text-start text-dark text-decoration-none p-3 d-flex align-items-center justify-content-between"
                                type="button"
                                onClick={() => {
                                  const el = document.getElementById(`collapse-inv-${index}`);
                                  if (el) el.classList.toggle('show');
                                  const icon = document.getElementById(`icon-inv-${index}`);
                                  if (icon) {
                                    icon.classList.toggle('mdi-chevron-down');
                                    icon.classList.toggle('mdi-chevron-up');
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center flex-grow-1">
                                  <div className="avatar-xs me-3">
                                    <span className="avatar-title rounded-circle bg-soft-info text-info font-size-12 fw-bold">
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div className="text-truncate" style={{ maxWidth: '350px' }}>
                                    <h6 className="mb-0 font-size-14 fw-bold text-dark text-truncate">
                                      {item.title}
                                    </h6>
                                    <p className="text-muted small mb-0">{item.sku}</p>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center">
                                  {item.price && (
                                    <span className="text-secondary fw-bold font-size-13 me-3">
                                      AED {item.price}
                                    </span>
                                  )}
                                  <i id={`icon-inv-${index}`} className="mdi mdi-chevron-down fs-4 text-muted"></i>
                                </div>
                              </button>
                            </div>

                            <div 
                              id={`collapse-inv-${index}`} 
                              className="collapse" 
                              aria-labelledby={`heading-inv-${index}`}
                            >
                                <div className="card-body p-0 border-top bg-light/30">
                                  {item.amcItems && item.amcItems.length > 0 ? (
                                    <Table className="table table-sm align-middle mb-0 bg-transparent">
                                    <thead className="bg-light">
                                      <tr>
                                        <th className="ps-4 py-2 border-0 small text-muted">#</th>
                                        <th className="py-2 border-0 small text-muted">ITEM NAME</th>
                                        <th className="py-2 border-0 small text-muted">SKU</th>
                                        <th className="text-center py-2 border-0 small text-muted">QTY</th>
                                        <th className="text-end pe-5 py-2 border-0 small text-muted">PRICE</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.amcItems.map((subItem, sIdx) => (
                                        <tr key={sIdx}>
                                          <td className="ps-4 py-2 border-bottom-light small text-muted">{sIdx + 1}</td>
                                          <td className="py-2 border-bottom-light">
                                            <div className="small fw-medium text-dark" style={{ whiteSpace: 'normal', maxWidth: '200px' }}>{subItem.title}</div>
                                            {subItem.product_type && (
                                              <Badge color="soft-light" className="text-muted font-size-10 fw-normal">
                                                {subItem.product_type}
                                              </Badge>
                                            )}
                                          </td>
                                          <td className="py-2 border-bottom-light small text-muted">{subItem.sku}</td>
                                          <td className="text-center py-2 border-bottom-light font-size-12 fw-bold">{subItem.qty}</td>
                                          <td className="text-end pe-5 py-2 border-bottom-light font-size-12 text-secondary fw-medium">
                                            {subItem.price ? `AED ${subItem.price}` : '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                ) : (
                                  <div className="p-3 text-center text-muted small">
                                    No included items found for this AMC
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <i className="mdi mdi-folder-open-outline d-block display-4 mb-2 opacity-25"></i>
                          <h6 className="fw-normal">No AMC items found</h6>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Table className="table table-nowrap align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3 ps-3 border-0" style={{ fontSize: '11px', fontWeight: '700', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</th>
                          <th className="py-3 ps-3 border-0" style={{ fontSize: '11px', fontWeight: '700', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Item Info</th>
                          <th className="py-3 text-center border-0" style={{ fontSize: '11px', fontWeight: '700', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qty</th>
                          <th className="py-3 text-end pe-3 border-0" style={{ fontSize: '11px', fontWeight: '700', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.length > 0 ? (
                          filteredItems.map((item, index) => (
                            <tr key={index}>
                              <td className="ps-3 py-3 border-bottom-light">
                                <div className="d-flex align-items-center">
                                  {index + 1}
                                </div>
                              </td>
                              <td className="ps-3 py-3 border-bottom-light">
                                <div className="d-flex align-items-center">
                                  <div>
                                    <h6 className="text-truncate mb-0 font-size-14">{item.title}</h6>
                                    <p className="text-muted mb-0 font-size-12">{item.sku}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center py-3 border-bottom-light">
                                <span className={`badge ${item.qty > 0 ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'} rounded-pill px-3 py-2 font-size-12`}>
                                  {item.qty || 0}
                                </span>
                              </td>
                              <td className="text-end pe-3 py-3 border-bottom-light">
                                <span className="font-size-12 text-secondary fw-medium">
                                  {item.price ? `AED ${item.price}` : '-'}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-5">
                              <div className="text-muted">
                                <i className="mdi mdi-folder-open-outline d-block display-4 mb-2 opacity-25"></i>
                                <h6 className="fw-normal">No items found in {formatTabName(activeSubTab || 'this category')}</h6>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </>
            )}
          </TabPane>
          <TabPane tabId="history" className="p-4">
            <FormGroup className="mb-4">
              <Label for="historySearch" className="form-label fw-semibold text-muted small text-uppercase mb-2">
                Search SKU or Reference
              </Label>
              <div className="position-relative">
                <Input
                  type="text"
                  id="historySearch"
                  placeholder="SKU, TO#, or Service ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control ps-5 py-2"
                  style={{ borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: 'none' }}
                />
                <i className="mdi mdi-magnify position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-5"></i>
              </div>
            </FormGroup>

            {isLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading history...</p>
              </div>
            ) : (
              <div className="history-accordion">
                {(() => {
                  // Group history by SKU
                  const groupedHistory = {};
                  if (Array.isArray(technicianInventoryHistory)) {
                    technicianInventoryHistory.forEach(item => {
                      if (!groupedHistory[item.sku]) {
                        groupedHistory[item.sku] = [];
                      }
                      groupedHistory[item.sku].push(item);
                    });
                  }

                  const skus = Object.keys(groupedHistory).filter(sku => 
                    sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    groupedHistory[sku].some(h => 
                      h.to_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      h.service_id?.toString().includes(searchTerm)
                    )
                  );

                  if (skus.length === 0) {
                    return (
                      <div className="text-center py-5 text-muted">
                        <i className="mdi mdi-history d-block display-4 mb-2 opacity-25"></i>
                        <h6 className="fw-normal">No history matches your search</h6>
                      </div>
                    );
                  }

                  return skus.map((sku, idx) => {
                    const historyItems = groupedHistory[sku];
                    const totalAdded = historyItems.filter(h => h.item_mode === 1).reduce((sum, h) => sum + Number(h.qty), 0);
                    const totalUsed = historyItems.filter(h => h.item_mode === 2).reduce((sum, h) => sum + Number(h.qty), 0);
                    
                    return (
                      <div key={sku} className="card border shadow-none mb-2 overflow-hidden" style={{ borderRadius: '12px' }}>
                        <div 
                          className="card-header bg-white p-0 border-0" 
                          id={`heading${idx}`}
                        >
                          <button 
                            className="btn btn-link w-100 text-start text-dark text-decoration-none p-3 d-flex align-items-center justify-content-between"
                            type="button"
                            onClick={() => {
                              const el = document.getElementById(`collapse${idx}`);
                              el.classList.toggle('show');
                              const icon = document.getElementById(`icon${idx}`);
                              icon.classList.toggle('mdi-chevron-down');
                              icon.classList.toggle('mdi-chevron-up');
                            }}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <div className="avatar-xs me-3">
                                <span className="avatar-title rounded-circle bg-soft-light text-secondary font-size-12">
                                  {idx + 1}
                                </span>
                              </div>
                              <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                <h6 className="mb-0 font-size-14 fw-bold text-dark text-truncate d-flex align-items-center">
                                  {historyItems[0]?.title || 'Unknown Item'}
                                  {historyItems[0]?.product_type && (
                                    <Badge color="soft-info" className="ms-2 text-info fs-8 fw-medium">
                                      {historyItems[0].product_type}
                                    </Badge>
                                  )}
                                </h6>
                                <p className="text-muted small mb-0">{sku}</p>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <div 
                                className="mx-2" 
                                style={{ 
                                  height: '24px', 
                                  borderLeft: '2px solid #556ee6', 
                                  opacity: 0.3 
                                }}
                              ></div>
                              <div className="text-end me-3">
                                <div className="font-size-11 fw-bold text-success">
                                  +{totalAdded} Added
                                </div>
                                <div className="font-size-11 fw-bold text-danger">
                                  {totalUsed} Used
                                </div>
                              </div>
                              <i id={`icon${idx}`} className="mdi mdi-chevron-down fs-4 text-muted"></i>
                            </div>
                          </button>
                        </div>

                        <div 
                          id={`collapse${idx}`} 
                          className="collapse" 
                          aria-labelledby={`heading${idx}`}
                        >
                          <div className="card-body p-0 border-top bg-light/30">
                            <Table className="table table-sm table-nowrap align-middle mb-0 bg-transparent">
                              <thead className="bg-light">
                                <tr>
                                  <th className="ps-4 py-2 border-0 small text-muted">DATE</th>
                                  <th className="text-center py-2 border-0 small text-muted">QTY</th>
                                  <th className="text-center py-2 border-0 small text-muted">MODE</th>
                                  <th className="py-2 border-0 small text-muted">REFERENCE</th>
                                </tr>
                              </thead>
                              <tbody>
                                {historyItems.map((h, hIdx) => (
                                  <tr key={hIdx}>
                                    <td className="ps-4 py-2 border-bottom-light">
                                      <div className="small fw-medium">{moment(h.created_at).format('DD MMM, hh:mm A')}</div>
                                    </td>
                                    <td className="text-center py-2 border-bottom-light fw-bold">{h.qty}</td>
                                    <td className="text-center py-2 border-bottom-light">
                                      <Badge 
                                        color={h.item_mode === 1 ? 'soft-success' : 'soft-danger'} 
                                        className={`${h.item_mode === 1 ? 'text-success' : 'text-danger'} rounded-pill`}
                                      >
                                        {h.item_mode === 1 ? 'Added' : 'Used'}
                                      </Badge>
                                    </td>
                                    <td className="py-2 border-bottom-light">
                                      <div className="small">
                                        {h.item_mode === 1 && h.to_number && (
                                          <div><span className="text-muted">TO:</span> <span className="text-primary">{h.to_number}</span></div>
                                        )}
                                        {h.item_mode === 2 && h.service_id && (
                                          <div><span className="text-muted">SR:</span> <span className="text-info">{h.service_id}</span></div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </TabPane>
        </TabContent>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ViewItems;


